// Firestore document shapes for the school module. Keep this file and
// firestore.rules' school-module match blocks in sync by hand — no schema
// codegen here (same convention as lib/tokens.js's TOKEN SHAPE comment).
// Every date is a "YYYY-MM-DD" string in the family's local timezone, never
// a Firestore Timestamp — see calendarMath.js.
//
// Ported from school-site's src/data/schema.ts, re-keyed to Grove's
// email-based identity: a school-site Assignment.studentId (a Firebase Auth
// uid) becomes studentEmail here (a children/{id}.studentEmail value) — see
// firestore.rules' isHomeschoolStudent().

// ─── FAMILY CALENDAR ──────────────────────────────────────────────────────────
// Reuses Grove's existing config/{id} subcollection (families/{id}/config/
// appConfig is already there for house settings) — no new rules match block
// needed, config/{id} is already public-read/owner-write.
// families/{id}/config/schoolCalendar: {
//   startDate:   string,          // "2026-08-17"
//   endDate:     string,          // "2027-05-21"
//   weeks:       string[],        // Monday of each instructional week, in order
//   holidays:    Record<string,string>,  // "2026-09-07" -> "Labor Day"
//   dayStart:    string,          // "HH:MM" default school-day start
//   anchors:     Record<string, { days: number[], start: string, mins: number, label: string, color: string }>,
//   totalInstructionalDays: number, // 179
//   gcalCalendarId?: string,       // set once the owner opts into anchor sync
//   gcalEventIds?: Record<string, string>, // anchorId -> GCal recurring
//                                   // master event id, so re-sync patches
//                                   // instead of creating duplicates — see
//                                   // gcalSync.js
// }

// ─── COURSE ─────────────────────────────────────────────────────────────────
// families/{id}/courses/{courseId}: {
//   id: string, name: string, subjectArea: string,
//   colorToken: string,  // "--c-math", etc — see theme/tokens.css
//   notes?: string,
// }

// ─── ASSIGNMENT ───────────────────────────────────────────────────────────────
// families/{id}/assignments/{assignmentId}: {
//   id: string,               // "{date}__{blockId}__{itemId}" for fixed/dated
//                              // items, "{weekNumber}__{blockId}__{itemId}"
//                              // for still-unplaced weekly-board tiles —
//                              // deterministic either way, so re-seeding/
//                              // re-syncing upserts, never duplicates
//   studentEmail: string,     // the owning child's children/{id}.studentEmail
//   courseId: string,
//   date: string | null,      // null until a student-placed tile is placed
//   weekNumber: number, dayOfWeek: number | null, // 1=Mon..5=Fri, null if unplaced
//   block: 'launch'|'math'|'la'|'logic'|'history'|'afternoon'|'fixed'|'calendar',
//   order: number | null,     // null if unplaced
//   title: string, subLabel?: string, resourceLink?: string,
//   estMinutes: number, isFloor: boolean,
//   placementMode: 'fixed' | 'student-placed', // 'fixed': date set at
//                              // creation, student can't move it. 'student-
//                              // placed': the weekly-board model — created
//                              // with date: null; the owning student sets
//                              // date/dayOfWeek/order on the week board (see
//                              // WeekBoard.jsx / NeedsADay.jsx), and may move or
//                              // unplace it again afterwards (see
//                              // firestore.rules' placementMode guard)
//   needsParent: boolean,     // default false — true when a parent has to be
//                              // present to DO the work (scribing, reading
//                              // aloud, lab setup), distinct from needsReview
//                              // below (which is about reviewing finished
//                              // work). Surfaced against config/dailyWindows.
//   avoidDays?: number[],      // weekly-board-only — days this tile shouldn't
//                              // land on (1=Mon..5=Fri), e.g. Mechanics can't
//                              // use Mon/Wed since the tutor has those. Soft:
//                              // checkSequencingRules warns, never blocks.
//   deliveryMode?: 'independent' | 'tool' | 'person', // informational UI hint
//                              // only ("🔊 read aloud" / "⌨️ dictate ok" /
//                              // "🧑 needs a person") — no integration behind
//                              // it, just a label sourced at seed/author time
//   status: 'not_started'|'in_progress'|'complete'|'flagged',
//   studentNote?: string, studentQuestion?: string, confusionFlag: boolean,
//   screenshotURL?: string, screenshotUploadedAt?: string, completedAt?: string,
//   needsReview: boolean,      // default true — set false for lean assignments
//                              // that don't warrant a parent look (e.g. free
//                              // reading); owner-set at authoring time, not
//                              // derivable from other fields
//   reviewedByParent: boolean, parentFeedback?: string,
//   source: 'seed'|'csv_import'|'manual'|'calendar_sync',
//   createdAt: string, updatedAt: string,
//   externalEventId?: string, externalSource?: 'google_calendar_ics', externalUpdatedAt?: string,
// }

export const BLOCK_IDS = ['launch', 'math', 'la', 'logic', 'history', 'afternoon', 'fixed'];

// Fields a homeschool student's Google-authenticated session is allowed to
// write on their own assignment doc. Mirrors studentEditableKeys() in
// firestore.rules — if you add a field here, add it there too.
export const STUDENT_WRITABLE_FIELDS = [
  'status',
  'studentNote',
  'studentQuestion',
  'confusionFlag',
  'screenshotURL',
  'screenshotUploadedAt',
  'completedAt',
  'updatedAt',
];

// ─── ATTENDANCE DAY ───────────────────────────────────────────────────────────
// UNUSED. Nothing reads or writes this collection — AttendanceTab derives every
// number live from assignments + the calendar (see reporting.js's
// attendanceStats). api.js's listenAttendance/upsertAttendanceDay are likewise
// uncalled. Kept as a documented shape in case attendance is ever snapshotted
// rather than derived; don't assume it holds anything today.
// families/{id}/attendanceLog/{date}: {
//   date: string, weekNumber: number, quarter: number,
//   blocksCompleted: Record<string, boolean>,
//   plannedMinutes: number, completedMinutes: number,
//   countsTowardDays: boolean, hardDayFlag: boolean, updatedAt: string,
// }

// ─── IMPORT BATCH ─────────────────────────────────────────────────────────────
// families/{id}/importBatches/{id}: {
//   id: string, filename: string, importedBy: string, // owner email
//   rowCount: number, createdAt: string,
//   rawRows: Record<string,string>[],
// }

// ─── DAILY WINDOWS ("he books you") ────────────────────────────────────────────
// Piggybacks on Grove's existing config/{id} subcollection, same as
// schoolCalendar — no new rules match block needed.
// families/{id}/config/dailyWindows: {
//   date: string,        // "YYYY-MM-DD" — today's date when last posted
//   windows: string[],   // e.g. ["9:30", "11:00", "1:30"] — free-text time
//                         // labels the parent posts each morning for
//                         // needsParent items (scribing, read-aloud, lab)
// }

// ─── SCHOOL MILESTONES ──────────────────────────────────────────────────────────
// Same config/{id} reuse.
// families/{id}/config/schoolMilestones: {
//   q1: {text: string, done: boolean}[], q2: [...], q3: [...], q4: [...],
//                         // per-quarter checkpoint bullets, owner-editable
//                         // and owner-checkable, not derived
// }
